import React, { useEffect, useRef, useState } from 'react';
import './Roadmap.css';
import SEO from './SEO';
import {
  FlagIcon,
  RocketLaunchIcon,
  UserGroupIcon,
  TrophyIcon,
  LightBulbIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  AcademicCapIcon,
  ChartBarIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

// Ecosystem apps data structure with existing and proposed apps
const ecosystemApps = [
  {
    id: 'learning-platform',
    name: 'Learning Platform',
    description: 'Complete e-learning solution with course creation, management, and certification.',
    features: ['Multi-tenant architecture', 'Course builder', 'Certificates', 'Free access'],
    status: 'Available',
    launchYear: 2023,
    icon: AcademicCapIcon,
    url: 'https://kursa.csl-brands.com',
    color: 'bg-green-500',
    category: 'Education'
  },
  {
    id: 'restaurant-saas',
    name: 'Restaurant SaaS',
    description: 'Complete restaurant management system with ordering, delivery, and analytics.',
    features: ['Order management', 'Delivery tracking', 'Analytics dashboard', 'Multi-location'],
    status: 'Coming Soon',
    launchYear: 2025,
    icon: BuildingOfficeIcon,
    url: '#',
    color: 'bg-orange-500',
    category: 'Hospitality'
  },
  {
    id: 'erp-crm',
    name: 'ERP/CRM Platform',
    description: 'Comprehensive business management solution for enterprises and SMEs.',
    features: ['Customer management', 'Inventory control', 'Financial reporting', 'Workflow automation'],
    status: 'In Development',
    launchYear: 2026,
    icon: ChartBarIcon,
    url: '#',
    color: 'bg-blue-500',
    category: 'Business'
  },
  {
    id: 'logistics-platform',
    name: 'Logistics Platform',
    description: 'End-to-end logistics management system for transportation and warehousing.',
    features: ['Fleet management', 'Route optimization', 'Inventory tracking', 'Real-time analytics'],
    status: 'Planned',
    launchYear: 2026,
    icon: GlobeAltIcon,
    url: '#',
    color: 'bg-indigo-500',
    category: 'Transportation'
  },
  {
    id: 'construction-management',
    name: 'Construction Management',
    description: 'Project management solution for construction companies and contractors.',
    features: ['Project planning', 'Resource allocation', 'Budget tracking', 'Safety compliance'],
    status: 'Planned',
    launchYear: 2027,
    icon: BuildingOfficeIcon,
    url: '#',
    color: 'bg-yellow-500',
    category: 'Construction'
  },
  {
    id: 'energy-monitoring',
    name: 'Energy Monitoring',
    description: 'Real-time energy consumption monitoring and optimization for industries.',
    features: ['Usage analytics', 'Cost optimization', 'Sustainability metrics', 'Compliance reporting'],
    status: 'Proposed',
    launchYear: 2027,
    icon: SparklesIcon,
    url: '#',
    color: 'bg-teal-500',
    category: 'Energy'
  },
  {
    id: 'port-management',
    name: 'Port Management',
    description: 'Comprehensive port operations and logistics management system.',
    features: ['Vessel scheduling', 'Cargo tracking', 'Resource allocation', 'Customs integration'],
    status: 'Proposed',
    launchYear: 2028,
    icon: GlobeAltIcon,
    url: '#',
    color: 'bg-cyan-500',
    category: 'Maritime'
  },
  {
    id: 'oil-production',
    name: 'Oil & Fats Production',
    description: 'Specialized management system for oils and fats production facilities.',
    features: ['Quality control', 'Production planning', 'Supply chain management', 'Compliance'],
    status: 'Proposed',
    launchYear: 2028,
    icon: LightBulbIcon,
    url: '#',
    color: 'bg-amber-500',
    category: 'Manufacturing'
  }
];

// Milestone data structure with past, present and future milestones
const milestones = [
  {
    id: 1,
    year: 2018,
    title: "Foundation",
    description: "CSL Brands was founded with a vision to revolutionize education and certification in Africa.",
    icon: FlagIcon,
    category: "company",
    achievements: ["Company registration", "Core team formation", "Initial funding secured"],
    metrics: { team: 5, countries: 1 },
    image: "foundation.jpg",
    side: "left",
    completed: true
  },
  {
    id: 2,
    year: 2019,
    title: "First Platform Launch",
    description: "Launched our first learning management system focused on certification courses.",
    icon: RocketLaunchIcon,
    category: "product",
    achievements: ["Beta platform release", "First 10 courses developed", "Technical infrastructure established"],
    metrics: { users: 100, courses: 10 },
    image: "platform-launch.jpg",
    side: "right",
    completed: true
  },
  {
    id: 3,
    year: 2020,
    title: "Market Expansion",
    description: "Expanded operations to multiple countries across Central and West Africa.",
    icon: GlobeAltIcon,
    category: "growth",
    achievements: ["Entered 3 new markets", "Localized platform", "Regional partnerships"],
    metrics: { countries: 4, partners: 5 },
    image: "expansion.jpg",
    side: "left",
    completed: true
  },
  {
    id: 4,
    year: 2021,
    title: "ISO Certification",
    description: "Achieved ISO 9001/2015 certification, validating our quality management systems.",
    icon: TrophyIcon,
    category: "recognition",
    achievements: ["Quality management system implementation", "Successful audit", "Certification awarded"],
    metrics: { certifications: 1, compliance: "100%" },
    image: "iso-cert.jpg",
    side: "right",
    completed: true
  },
  {
    id: 5,
    year: 2022,
    title: "Enterprise Solutions",
    description: "Launched enterprise solutions for businesses and government organizations.",
    icon: BuildingOfficeIcon,
    category: "product",
    achievements: ["Enterprise platform development", "First government contract", "Corporate training programs"],
    metrics: { enterpriseClients: 5, revenue: "+120%" },
    image: "enterprise.jpg",
    side: "left",
    completed: true
  },
  {
    id: 6,
    year: 2023,
    title: "Learning Ecosystem",
    description: "Developed comprehensive ecosystem of integrated learning and certification tools.",
    icon: AcademicCapIcon,
    category: "product",
    achievements: ["Platform integration", "Mobile app launch", "API development"],
    metrics: { tools: 5, integrations: 12 },
    image: "ecosystem.jpg",
    side: "right",
    completed: true
  },
  {
    id: 7,
    year: 2024,
    title: "Industry Recognition",
    description: "Recognized as a leading EdTech provider in Central Africa.",
    icon: ChartBarIcon,
    category: "recognition",
    achievements: ["EdTech Innovation Award", "Featured in industry publications", "Speaker invitations"],
    metrics: { awards: 2, mediaFeatures: 8 },
    image: "recognition.jpg",
    side: "left",
    completed: true
  },
  {
    id: 8,
    year: 2025,
    title: "Current Growth",
    description: "Expanding our platform capabilities and reaching new markets.",
    icon: SparklesIcon,
    category: "growth",
    achievements: ["New feature rollouts", "Team expansion", "Investment round"],
    metrics: { growth: "85%", team: 50 },
    image: "current.jpg",
    side: "right",
    completed: true
  },
  {
    id: 9,
    year: 2026,
    title: "AI-Powered Learning",
    description: "Implementing advanced AI to personalize learning experiences.",
    icon: LightBulbIcon,
    category: "innovation",
    achievements: ["AI research partnership", "Personalized learning paths", "Adaptive assessments"],
    metrics: { developmentProgress: "60%" },
    image: "ai-future.jpg",
    side: "left",
    completed: false
  },
  {
    id: 10,
    year: 2027,
    title: "Global Expansion",
    description: "Planned expansion to international markets beyond Africa.",
    icon: GlobeAltIcon,
    category: "growth",
    achievements: ["Market research", "International partnerships", "Localization strategy"],
    metrics: { targetCountries: 15 },
    image: "global.jpg",
    side: "right",
    completed: false
  }
];

// Tailwind CSS animations will be used instead of animation variants

// Component for individual milestone
const Milestone = ({ milestone, index }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.disconnect();
      }
    };
  }, []);

  return (
    <div
      ref={ref}
      className={`flex items-center mb-16 ${milestone.side === 'left'
          ? 'md:flex-row'
          : 'md:flex-row-reverse'
        } flex-col`}
    >
      {/* Timeline dot */}
      <div
        className={`hidden md:block absolute left-1/2 transform -translate-x-1/2 ${isVisible ? 'animate-scale-in' : 'opacity-0 scale-0'
          }`}
        style={{ animationDelay: '0.2s' }}
      >
        <div className={`w-6 h-6 rounded-full border-4 ${milestone.completed
            ? 'bg-csl-green border-csl-dark-green'
            : 'bg-gray-300 border-gray-400'
          } z-10`}></div>
      </div>

      {/* Content card */}
      <div
        className={`w-full md:w-5/12 bg-white p-6 rounded-xl shadow-lg ${milestone.side === 'left'
            ? 'md:mr-8'
            : 'md:ml-8'
          } ${isVisible ? 'animate-fade-in' : 'opacity-0'
          }`}
        style={{
          animationDelay: '0.1s',
          transform: isVisible ? 'translateX(0)' : `translateX(${milestone.side === 'left' ? '-50px' : '50px'})`
        }}
      >
        <div className="flex items-center mb-4">
          <div className={`p-3 rounded-full ${milestone.completed
              ? 'bg-csl-green text-white'
              : 'bg-gray-200 text-gray-500'
            } mr-4`}>
            <milestone.icon className="h-6 w-6" />
          </div>
          <div>
            <span className={`text-sm font-bold ${milestone.completed
                ? 'text-csl-green'
                : 'text-gray-400'
              }`}>
              {milestone.year}
            </span>
            <h3 className="text-xl font-bold">{milestone.title}</h3>
          </div>
        </div>

        <p className="text-gray-600 mb-4">{milestone.description}</p>

        <div className="mb-4">
          <h4 className="font-semibold mb-2">Key Achievements:</h4>
          <ul className="space-y-1">
            {milestone.achievements.map((achievement, i) => (
              <li key={i} className="flex items-center">
                <span className={`w-2 h-2 rounded-full ${milestone.completed
                    ? 'bg-csl-green'
                    : 'bg-gray-300'
                  } mr-2`}></span>
                <span className="text-sm">{achievement}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Metrics */}
        <div className="flex flex-wrap gap-3">
          {Object.entries(milestone.metrics).map(([key, value]) => (
            <div
              key={key}
              className={`px-3 py-1 rounded-full text-xs font-medium ${milestone.completed
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-600'
                }`}
            >
              {key}: {value}
            </div>
          ))}
        </div>
      </div>

      {/* Empty space for timeline alignment */}
      <div className="hidden md:block w-5/12"></div>
    </div>
  );
};

// Progress indicator component
const ProgressIndicator = ({ progress }) => {
  return (
    <div className="fixed top-20 right-8 h-64 w-2 bg-gray-200 rounded-full z-50 hidden lg:block">
      <div
        className="w-full bg-csl-green rounded-full"
        style={{ height: `${progress}%`, originY: 0 }}
      ></div>
    </div>
  );
};

// Navigation dots component
const NavigationDots = ({ milestones, activeIndex, setActiveIndex }) => {
  return (
    <div className="fixed top-1/2 right-8 transform -translate-y-1/2 flex flex-col space-y-4 z-50 hidden lg:block">
      {milestones.map((milestone, index) => (
        <button
          key={milestone.id}
          className={`w-3 h-3 rounded-full ${index <= activeIndex
              ? 'bg-csl-green'
              : 'bg-gray-300'
            } transition-transform duration-300 hover:scale-150`}
          onClick={() => {
            const element = document.getElementById(`milestone-${milestone.id}`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth' });
              setActiveIndex(index);
            }
          }}
        />
      ))}
    </div>
  );
};

// EcosystemApp component for displaying individual apps
const EcosystemApp = ({ app }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.disconnect();
      }
    };
  }, []);

  // Determine status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Available':
        return 'bg-green-100 text-green-800';
      case 'Coming Soon':
        return 'bg-orange-100 text-orange-800';
      case 'In Development':
        return 'bg-blue-100 text-blue-800';
      case 'Planned':
        return 'bg-indigo-100 text-indigo-800';
      case 'Proposed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div
      ref={ref}
      className={`bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl ${isVisible ? 'animate-slide-up animate-fade-in' : 'opacity-0 translate-y-8'}`}
      style={{ animationDelay: '0.1s' }}
    >
      <div className={`h-2 ${app.color}`}></div>
      <div className='p-6'>
        <div className='flex items-center mb-4'>
          <div className={`p-3 rounded-full ${app.color} bg-opacity-10 mr-4`}>
            <app.icon className='h-6 w-6 text-gray-700' />
          </div>
          <div>
            <h3 className='text-xl font-bold'>{app.name}</h3>
            <div className={`text-xs font-semibold px-2 py-1 rounded-full inline-block ${getStatusColor(app.status)}`}>
              {app.status} {app.launchYear ? `• ${app.launchYear}` : ''}
            </div>
          </div>
        </div>

        <p className='text-gray-600 mb-4'>{app.description}</p>

        <div className='mb-4'>
          <h4 className='font-semibold mb-2 text-sm'>Key Features:</h4>
          <ul className='space-y-1'>
            {app.features.map((feature, i) => (
              <li key={i} className='flex items-center'>
                <span className={`w-2 h-2 rounded-full bg-gray-300 mr-2`}></span>
                <span className='text-sm'>{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className='mt-4 pt-4 border-t border-gray-100'>
          <span className='text-xs font-medium text-gray-500 uppercase tracking-wider'>
            Category: {app.category}
          </span>

          {app.status === 'Available' && (
            <a
              href={app.url}
              className='mt-3 inline-block px-4 py-2 bg-csl-green text-white text-sm font-medium rounded-lg hover:bg-csl-dark-green transition-all duration-300 hover:scale-105 active:scale-95'
            >
              Explore App
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const Roadmap = () => {
  const [scrollProgress, setScrollProgress] = React.useState(0);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [activeTab, setActiveTab] = React.useState('timeline');
  const [timelineVisible, setTimelineVisible] = useState(false);
  const timelineRef = useRef(null);
  const ecosystemRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimelineVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (timelineRef.current) {
      observer.observe(timelineRef.current);
    }

    return () => {
      if (timelineRef.current) {
        observer.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (timelineRef.current) {
        const scrollPosition = window.scrollY;
        const windowHeight = window.innerHeight;
        const docHeight = document.body.clientHeight;
        const totalScrollable = docHeight - windowHeight;
        const progress = (scrollPosition / totalScrollable) * 100;
        setScrollProgress(progress);

        // Update active milestone based on scroll position
        const milestoneElements = document.querySelectorAll('[id^="milestone-"]');
        milestoneElements.forEach((element, index) => {
          const rect = element.getBoundingClientRect();
          if (rect.top <= windowHeight / 2 && rect.bottom >= windowHeight / 2) {
            setActiveIndex(index);
          }
        });
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Group ecosystem apps by status
  const groupedApps = {
    available: ecosystemApps.filter(app => app.status === 'Available'),
    comingSoon: ecosystemApps.filter(app => app.status === 'Coming Soon'),
    inDevelopment: ecosystemApps.filter(app => app.status === 'In Development'),
    planned: ecosystemApps.filter(app => app.status === 'Planned'),
    proposed: ecosystemApps.filter(app => app.status === 'Proposed')
  };

  return (
    <section className="bg-gray-50 py-20">
      <SEO
        path="/roadmap"
        title="Product Roadmap | CSL Brands"
        description="Discover the CSL Brands ecosystem roadmap — the products and milestones shaping digital transformation across Africa."
        image="/CSL-Brands-Integrated-Business-Solutions.jpg"
        keywords="CSL Brands roadmap, product roadmap, KURSA, PrimeCorn, TchopNjoh, ShopiKat, tech ecosystem Africa, edtech roadmap, feuille de route"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero section */}
        <div
          className="text-center mb-16 animate-slide-up animate-fade-in"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Our Journey and Vision
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore CSL Brands' evolution from inception to our future vision.
            Discover our milestones, achievements, and the path ahead as we
            continue to revolutionize education and certification in Africa.
          </p>
        </div>

        {/* Tab navigation */}
        <div className="flex justify-center mb-12">
          <div
            className="inline-flex bg-white rounded-lg p-1 shadow-md animate-slide-up animate-fade-in"
            style={{ animationDelay: '0.2s' }}
          >
            <button
              onClick={() => setActiveTab('timeline')}
              className={`px-6 py-3 rounded-lg text-sm font-medium transition-all duration-300 ${activeTab === 'timeline' ? 'bg-csl-green text-white shadow-md' : 'text-gray-600 hover:text-gray-900'}`}
            >
              Company Timeline
            </button>
            <button
              onClick={() => setActiveTab('ecosystem')}
              className={`px-6 py-3 rounded-lg text-sm font-medium transition-all duration-300 ${activeTab === 'ecosystem' ? 'bg-csl-green text-white shadow-md' : 'text-gray-600 hover:text-gray-900'}`}
            >
              Ecosystem Apps
            </button>
          </div>
        </div>

        {/* Timeline View */}
        {activeTab === 'timeline' && (
          <div className="relative" ref={timelineRef}>
            {/* Center line */}
            <div
              className={`absolute left-1/2 transform -translate-x-1/2 w-1 bg-gray-200 h-full hidden md:block ${timelineVisible ? 'animate-scale-in-slow' : 'scale-y-0'}`}
              style={{ transformOrigin: "top" }}
            ></div>

            {/* Milestones */}
            <div className="relative z-10">
              {milestones.map((milestone, index) => (
                <div
                  key={milestone.id}
                  id={`milestone-${milestone.id}`}
                  style={{ animationDelay: `${index * 0.3}s` }}
                >
                  <Milestone milestone={milestone} index={index} />
                </div>
              ))}
            </div>

            {/* Vision section */}
            <div 
              className="text-center mt-20 bg-white p-8 rounded-2xl shadow-lg animate-fade-in animate-slide-up"
              style={{ animationDelay: '0.3s' }}
            >
              <h2 className="text-3xl font-bold mb-6">Our Vision for the Future</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
                At CSL Brands, we're committed to transforming education and certification 
                across Africa and beyond. Our roadmap is guided by innovation, quality, 
                and accessibility, as we continue to build solutions that empower individuals 
                and organizations.
              </p>
              <button
                className="px-8 py-4 bg-csl-green text-white font-semibold rounded-xl hover:bg-csl-dark-green transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
                onClick={() => {
                  window.location.href = 'https://kursa.csl-brands.com';
                }}
              >
                Join Our Journey
              </button>
            </div>
          </div>
        )}

        {/* Ecosystem Apps View */}
        {activeTab === 'ecosystem' && (
          <div ref={ecosystemRef}>
            {/* Ecosystem intro */}
            <div
              className="text-center mb-12 animate-fade-in animate-slide-up"
            >
              <h2 className="text-3xl font-bold mb-4">CSL Brands Ecosystem</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Our growing ecosystem of integrated applications serves diverse industries across Africa.
                From education to logistics, we're building solutions that address real business challenges.
              </p>
            </div>
            
            {/* Available Apps */}
            <div className="mb-16">
              <h3 
                className="text-2xl font-bold mb-6 border-l-4 border-green-500 pl-4 animate-fade-in"
                style={{ animationDelay: '0.2s' }}
              >
                Available Now
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {groupedApps.available.map((app) => (
                  <EcosystemApp key={app.id} app={app} />
                ))}
              </div>
            </div>

            {/* Coming Soon Apps */}
            <div className="mb-16">
              <h3
                className="text-2xl font-bold mb-6 border-l-4 border-orange-500 pl-4 animate-fade-in"
                style={{ animationDelay: '0.2s' }}
              >
                Coming Soon
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {groupedApps.comingSoon.map((app) => (
                  <EcosystemApp key={app.id} app={app} />
                ))}
              </div>
            </div>

            {/* In Development Apps */}
            <div className="mb-16">
              <h3
                className="text-2xl font-bold mb-6 border-l-4 border-blue-500 pl-4 animate-fade-in"
                style={{ animationDelay: '0.3s' }}
              >
                In Development
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {groupedApps.inDevelopment.map((app) => (
                  <EcosystemApp key={app.id} app={app} />
                ))}
              </div>
            </div>

            {/* Future Roadmap */}
            <div className="mb-16">
              <h3
                className="text-2xl font-bold mb-6 border-l-4 border-purple-500 pl-4 animate-fade-in"
                style={{ animationDelay: '0.4s' }}
              >
                Future Roadmap
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...groupedApps.planned, ...groupedApps.proposed].map((app) => (
                  <EcosystemApp key={app.id} app={app} />
                ))}
              </div>
            </div>

            {/* Industry Focus */}
            <div
              className="text-center mt-20 bg-white p-8 rounded-2xl shadow-lg animate-fade-in animate-slide-up"
              style={{ animationDelay: '0.5s' }}
            >
              <h2 className="text-3xl font-bold mb-6">Industry-Focused Solutions</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
                Our ecosystem apps are designed to address the unique challenges of key industries
                across Africa including Oil & Fats, Energy, Transport & Logistics, Port Operations,
                and Construction. Each solution is built with industry expertise and local knowledge.
              </p>
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <span className="px-4 py-2 bg-gray-100 text-gray-800 rounded-full font-medium">Oils & Fats</span>
                <span className="px-4 py-2 bg-gray-100 text-gray-800 rounded-full font-medium">Energy & Mining</span>
                <span className="px-4 py-2 bg-gray-100 text-gray-800 rounded-full font-medium">Transport & Logistics</span>
                <span className="px-4 py-2 bg-gray-100 text-gray-800 rounded-full font-medium">Port Operations</span>
                <span className="px-4 py-2 bg-gray-100 text-gray-800 rounded-full font-medium">Construction (BTP)</span>
              </div>
              <button
                className="px-8 py-4 bg-csl-green text-white font-semibold rounded-xl hover:bg-csl-dark-green transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
                onClick={() => {
                  window.location.href = 'https://cfpcsl.com/presentation-de-csl';
                }}
              >
                Explore Industries
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Progress indicator */}
      <ProgressIndicator progress={scrollProgress} />

      {/* Navigation dots */}
      <NavigationDots
        milestones={milestones}
        activeIndex={activeIndex}
        setActiveIndex={setActiveIndex}
      />
    </section>
  );
};

export default Roadmap;
